import {fetchFixturePrematchContext} from "./OddsTooltip";
import { readWorkflowResponse, saveTeamSettingsVerified } from "../helpers/workflowResponse";
import i18next from "i18next";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { fetchViaPost } from "../helpers/fetchViaPost";
import { Calculate, RestartAlt, Save } from "@mui/icons-material";
import { fetchFromApi } from "../helpers/fetchFromApi";
import { LeagueTable } from "./FixtureDetails/LeagueTable";
import {
  FEATURES,
  getFeatureAccess,
} from "../helpers/featureAccess";
import { StrengthRanking } from "./CompactLeagueTable";
import { computeInterdependentValues } from "../helpers/computeInterdependentValues";
import { loadPreviousLeagueSeason } from "../helpers/previousLeagueSeason";
import {
  AdminFixtureViewToggle,
  canUseFixtureWorkspace,
  FixtureWorkspacePanel,
} from "./FixtureWorkspacePanel";
import { ExperimentalFixtureWorkspace } from "./ExperimentalFixtureWorkspace";
import { FixtureFullView } from "./FixtureFullView";
import { canUseFixtureWorkspaceView } from "../helpers/fixtureWorkspaceRegistry";

const finiteValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const resolveFixtureModelBasis = ({ modelBasis, homeTeam, awayTeam, seasonParameters }) => {
  const storedFixtureFactor = finiteValue(modelBasis?.draw_factor);
  const homeFactor = finiteValue(homeTeam?.draw_factor);
  const awayFactor = finiteValue(awayTeam?.draw_factor);
  const seasonFactor = finiteValue(seasonParameters?.drawfactor);
  const calculatedFixtureFactor = homeFactor !== null && awayFactor !== null && seasonFactor > 0
    ? (homeFactor * awayFactor) / seasonFactor
    : null;
  return {
    drawFactor: storedFixtureFactor ?? calculatedFixtureFactor,
    goalAverage: finiteValue(modelBasis?.goal_average) ?? finiteValue(seasonParameters?.stored_goal_average),
  };
};

export const parseSeasonParametersResponse = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value !== "string" || value.trim() === "") return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const updateFixtureTeamStrength = (current, value) => {
  const strength = Number(value);
  if (!Number.isFinite(strength)) return current;

  const totalGoals = Number(current.for_total) + Number(current.against_total);
  const againstTotal = totalGoals / Math.abs(strength + 1);
  const forTotal = totalGoals - againstTotal;

  return {
    ...current,
    total_strength: strength.toFixed(3),
    for_home: forTotal * Number(current.for_homefactor),
    against_home: againstTotal * Number(current.against_homefactor),
    for_away: forTotal * (2 - Number(current.for_homefactor)),
    against_away: againstTotal * (2 - Number(current.against_homefactor)),
    against_total: againstTotal,
    for_total: forTotal,
  };
};

export const FixtureDetails = ({
  row,
  handleRecompile,
  onDraftReset,
  user,
  compactWorkspace = false,
  strengthRanking,
  onStrengthsSaved,
  strengthsRevision = 0,
  actualRows,
  expectedRows,
  shouldRows,
  marketRows,
  expectedLedger,
  shouldLedger,
  marketOdds,
  indicationThreshold,
}) => {
  const expandedWorkspaceAllowed = canUseFixtureWorkspace(user);
  const [actionError, setActionError] = useState("");
  const [loadedFixtureId, setLoadedFixtureId] = useState(null);
  async function runAction(action) {
    setActionError("");
    try { return await action(); }
    catch (error) { setActionError(error.message); return false; }
  }
  async function handleSave() {
    return runAction(async () => {
      const persistedTeam = ({ total_goals, rank, custom_name, ...team }) => team;
      const values = await saveTeamSettingsVerified([
        persistedTeam({...localTeamStrength, total_strength:+localTeamStrength.total_strength}),
        persistedTeam({...visitorTeamStrength, total_strength:+visitorTeamStrength.total_strength}),
      ]);
      const home = values[row.localTeam.id], away = values[row.visitorTeam.id];
      setLocalTeamStrength({...home, total_goals:Number(home.for_total)+Number(home.against_total)});
      setVisitorTeamStrength({...away, total_goals:Number(away.for_total)+Number(away.against_total)});
      setOriginalLocalTeamStrength(home);
      setOriginalVisitorTeamStrength(away);
      setOriginalLocalGoalSumExp({goal_sum_exp:Number((Number(home.for_total)+Number(home.against_total)).toFixed(3))});
      setOriginalVisitorGoalSumExp({goal_sum_exp:Number((Number(away.for_total)+Number(away.against_total)).toFixed(3))});
      setWorkspaceBaseline(null);
      setLocalTeamStrengthChanged(false);
      setLocalGoalSumExpChanged(false);
      setVisitorTeamStrengthChanged(false);
      setVisitorGoalSumExpChanged(false);
      await onStrengthsSaved?.();
      return true;
    });
  }

  useEffect(() => {
    let cancelled = false;
    async function fetchStrengths() {
      try {
        const teamStrengths = await readWorkflowResponse(
          await fetchViaPost(
            "get-team-strengths",
            JSON.stringify({
              season_id: row.season_id,
              team_ids: [row.localTeam.id, row.visitorTeam.id],
            }),
          )
        );
        if (cancelled) return;
        if (!teamStrengths[row.localTeam.id] || !teamStrengths[row.visitorTeam.id]) throw new Error("TEAM_SETTINGS_UNAVAILABLE");
        setLoadedFixtureId(row.id);
        setWorkspaceBaseline(null);

        var originalLocalTeamStrength = teamStrengths[row.localTeam.id];
        var originalVisitorTeamStrength = teamStrengths[row.visitorTeam.id];

        setOriginalVisitorTeamStrength(originalVisitorTeamStrength);
        setOriginalLocalTeamStrength(originalLocalTeamStrength);

        var localGoalSumExp =
          (
            originalLocalTeamStrength.against_total +
            originalLocalTeamStrength.for_total
          )?.toFixed(3) ?? "-";
        setOriginalLocalGoalSumExp({ goal_sum_exp: Number(localGoalSumExp) });
        setLocalGoalSumExp({
          goal_sum_exp: Number(localGoalSumExp),
        });

        var visitorGoalSumExp =
          (
            originalVisitorTeamStrength.against_total +
            originalVisitorTeamStrength.for_total
          )?.toFixed(3) ?? "-";
        setOriginalVisitorGoalSumExp({
          goal_sum_exp: Number(visitorGoalSumExp),
        });
        setVisitorGoalSumExp({
          goal_sum_exp: Number(visitorGoalSumExp),
        });

        setLocalTeamStrength({
          ...teamStrengths[row.localTeam.id],
          total_goals: Number(originalLocalTeamStrength.for_total) + Number(originalLocalTeamStrength.against_total),
        });
        setVisitorTeamStrength({
          ...teamStrengths[row.visitorTeam.id],
          total_goals: Number(originalVisitorTeamStrength.for_total) + Number(originalVisitorTeamStrength.against_total),
        });
      } catch (e) {
        if (!cancelled) setActionError(e.message);
      }
    }

    async function fetchStandings() {
      try {
        const response = await (
          await fetchFromApi(
            `fetch-data-v3/standings/seasons/${row.season_id}?include=participant;details`,
          )
        ).json();

        var standings;
        let data = response?.data;

        var found1 = data?.find((e) => e.participant_id === row.localTeam.id);
        let found2 = data?.find((e) => e.participant_id === row.visitorTeam.id);

        standings = [found1, found2].filter((e) => e !== undefined);

        const stats = standings.reduce((prev, e) => {
          const goals_scored =
            e.details.find((d) => d.type_id === 133)?.value ?? 0;
          const goals_against =
            e.details.find((d) => d.type_id === 134)?.value ?? 0;
          const games_played =
            e.details.find((d) => d.type_id === 129)?.value ?? 0;
          const won = e.details.find((d) => d.type_id === 130)?.value ?? 0;
          const lost = e.details.find((d) => d.type_id === 132)?.value ?? 0;
          const draw = e.details.find((d) => d.type_id === 131)?.value ?? 0;
          const points = e.details.find((d) => d.type_id === 187)?.value ?? 0;

          const home_games_played =
            e.details.find((d) => d.type_id === 135)?.value ?? 0;
          const home_won = e.details.find((d) => d.type_id === 136)?.value ?? 0;
          const home_draw =
            e.details.find((d) => d.type_id === 137)?.value ?? 0;
          const home_lost =
            e.details.find((d) => d.type_id === 138)?.value ?? 0;
          const home_goals_scored =
            e.details.find((d) => d.type_id === 139)?.value ?? 0;
          const home_goals_against =
            e.details.find((d) => d.type_id === 140)?.value ?? 0;
          const home_points =
            e.details.find((d) => d.type_id === 185)?.value ?? 0;

          const away_games_played =
            e.details.find((d) => d.type_id === 141)?.value ?? 0;
          const away_won = e.details.find((d) => d.type_id === 142)?.value ?? 0;
          const away_draw =
            e.details.find((d) => d.type_id === 143)?.value ?? 0;
          const away_lost =
            e.details.find((d) => d.type_id === 144)?.value ?? 0;
          const away_goals_scored =
            e.details.find((d) => d.type_id === 145)?.value ?? 0;
          const away_goals_against =
            e.details.find((d) => d.type_id === 146)?.value ?? 0;
          const away_points =
            e.details.find((d) => d.type_id === 186)?.value ?? 0;

          var homeDiff = 0;
          var awayDiff = 0;

          const overallDiff = goals_scored - goals_against;

          if (row.localTeam.id === e.participant_id) {
            setEarnedStrengthHome(goals_scored / goals_against);
            setEarnedGoalSumHome((goals_scored + goals_against) / games_played);
            homeDiff = home_goals_scored - home_goals_against;
          }
          if (row.visitorTeam.id === e.participant_id) {
            setEarnedStrengthAway(goals_scored / goals_against);
            setEarnedGoalSumAway((goals_scored + goals_against) / games_played);
            awayDiff = away_goals_scored - away_goals_against;
          }

          return {
            ...prev,
            [e.participant_id]: {
              league: {
                position: e.position || "-",
                games: games_played,
                won: won, //S
                draw: draw, //U
                lost: lost, //N
                goals: `${goals_scored}:${goals_against}`,
                diff: overallDiff > 0 ? `+${overallDiff}` : overallDiff,
                points: points,
              },
              home: {
                position: "-",
                games: home_games_played,
                won: home_won, //S
                draw: home_draw, //U
                lost: home_lost, //N
                goals: `${home_goals_scored}:${home_goals_against}`,
                diff: homeDiff > 0 ? `+${homeDiff}` : homeDiff,
                points: home_points,
              },
              away: {
                position: "-",
                games: away_games_played,
                won: away_won, //S
                draw: away_draw, //U
                lost: away_lost, //N
                goals: `${away_goals_scored}:${away_goals_against}`,
                diff: awayDiff > 0 ? `+${awayDiff}` : awayDiff,
                points: away_points,
              },
            },
          };
        }, {});

        setStats(stats);
      } catch (e) {
        //console.error(e);
      }
    }

    if (row.id) {
      fetchStrengths();
      fetchStandings();
    }
    return () => { cancelled = true; };
  }, [row.id, row.season_id, row.localTeam.id, row.visitorTeam.id, strengthsRevision]);
  const [localTeamStrength, setLocalTeamStrength] = useState();
  const [localTeamStrengthChanged, setLocalTeamStrengthChanged] =
    useState(false);
  const [localGoalSumExp, setLocalGoalSumExp] = useState();
  const [localGoalSumExpChanged, setLocalGoalSumExpChanged] = useState(false);
  const [earnedStrengthHome, setEarnedStrengthHome] = useState(null);
  const [earnedGoalSumHome, setEarnedGoalSumHome] = useState(null);
  const [earnedStrengthAway, setEarnedStrengthAway] = useState(null);
  const [earnedGoalSumAway, setEarnedGoalSumAway] = useState(null);
  const [stats, setStats] = useState([]);
  const [visitorTeamStrength, setVisitorTeamStrength] = useState();
  const [visitorTeamStrengthChanged, setVisitorTeamStrengthChanged] =
    useState(false);
  const [visitorGoalSumExp, setVisitorGoalSumExp] = useState();
  const [visitorGoalSumExpChanged, setVisitorGoalSumExpChanged] =
    useState(false);

  const [originalLocalTeamStrength, setOriginalLocalTeamStrength] = useState();
  const [originalLocalGoalSumExp, setOriginalLocalGoalSumExp] = useState();
  const [originalVisitorTeamStrength, setOriginalVisitorTeamStrength] =
    useState();
  const [originalVisitorGoalSumExp, setOriginalVisitorGoalSumExp] = useState();
  const [fixtureDraft, setFixtureDraft] = useState({
    homeGoalExp: "",
    awayGoalExp: "",
    drawFactor: "",
    goalAverage: "",
  });
  const [fixtureOverrideChanged, setFixtureOverrideChanged] = useState({
    homeGoalExp: false,
    awayGoalExp: false,
    drawFactor: false,
  });
  const [adminFixtureView, setAdminFixtureView] = useState("legacy");
  const [workspaceBaseline, setWorkspaceBaseline] = useState(null);
  const [draftCalculation, setDraftCalculation] = useState(null);
  const [draftReady, setDraftReady] = useState(false);
  const [fixtureEstimateSaved, setFixtureEstimateSaved] = useState(false);
  const [seasonParameters, setSeasonParameters] = useState(null);
  const [workspaceStrengthRanking, setWorkspaceStrengthRanking] = useState(strengthRanking || []);
  const [previousSeasonData, setPreviousSeasonData] = useState({ loading: false, season: null, actualRows: [] });
  const [marketDerivedPreview, setMarketDerivedPreview] = useState(null);
  const [marketPreviewReason, setMarketPreviewReason] = useState(null);
  const [performance, setPerformance] = useState({
    actualRows: actualRows || [],
    expectedRows: expectedRows || [],
    shouldRows: shouldRows || [],
    marketRows: marketRows || [],
  });
  const [previousView, setPreviousView] = useState("legacy");
  const previousScroll = useRef([]);
  const closeExperiment = () => {
    setAdminFixtureView(previousView);
    requestAnimationFrame(() => previousScroll.current.forEach(([element, left, top]) => element.scrollTo?.(left, top)));
  };
  const workspaceDataNeeded = expandedWorkspaceAllowed && adminFixtureView !== "legacy";

  useEffect(() => {
    setFixtureDraft({
      homeGoalExp: row.goalexp?.home ?? "",
      awayGoalExp: row.goalexp?.away ?? "",
      drawFactor: row.model_basis?.draw_factor ?? "",
      goalAverage: row.model_basis?.goal_average ?? "",
    });
    setFixtureOverrideChanged({ homeGoalExp: false, awayGoalExp: false, drawFactor: false });
  }, [row.id, row.goalexp?.home, row.goalexp?.away, row.model_basis?.draw_factor, row.model_basis?.goal_average]);

  useEffect(() => {
    let cancelled = false;
    fetchViaPost(
      "get-season-parameters",
      JSON.stringify({ season_id: Number(row.season_id), user_id: user?.id ?? user?.user_id }),
    )
      .then((response) => response.json())
      .then((parameters) => {
        if (!cancelled) setSeasonParameters(parseSeasonParametersResponse(parameters));
      })
      .catch(() => {
        if (!cancelled) setSeasonParameters(null);
      });
    return () => { cancelled = true; };
  }, [row.id, row.season_id, user?.id, user?.user_id]);

  useEffect(() => {
    const basis = resolveFixtureModelBasis({
      modelBasis: {
        draw_factor: row.model_basis?.draw_factor,
        goal_average: row.model_basis?.goal_average,
      },
      homeTeam: { draw_factor: localTeamStrength?.draw_factor },
      awayTeam: { draw_factor: visitorTeamStrength?.draw_factor },
      seasonParameters,
    });
    setFixtureDraft((current) => ({
      ...current,
      drawFactor: basis.drawFactor ?? "",
      goalAverage: basis.goalAverage ?? "",
    }));
  }, [row.id, row.model_basis?.draw_factor, row.model_basis?.goal_average, localTeamStrength?.draw_factor, visitorTeamStrength?.draw_factor, seasonParameters]);

  useEffect(() => {
    setAdminFixtureView("legacy");
    setWorkspaceBaseline(null);
    setDraftCalculation(null);
    setDraftReady(false);
    setFixtureEstimateSaved(false);
  }, [row.id]);

  useEffect(() => {
    if (!workspaceDataNeeded) return undefined;
    if (Array.isArray(strengthRanking) && strengthRanking.length) {
      setWorkspaceStrengthRanking(strengthRanking);
      return undefined;
    }
    let cancelled = false;
    fetchViaPost("get-team-strengths-by-season-id", JSON.stringify({ season_id: Number(row.season_id), league_id: Number(row.league_id ?? row.league?.id) }))
      .then((response) => response.json())
      .then((response) => {
        if (cancelled) return;
        const strengths = response?.strengths ?? response ?? {};
        setWorkspaceStrengthRanking(Object.entries(strengths).map(([teamId, settings]) => ({ ...settings, team_id: Number(teamId), team_name: settings?.team_name || settings?.custom_name || settings?.name })));
      })
      .catch(() => { if (!cancelled) setWorkspaceStrengthRanking([]); });
    return () => { cancelled = true; };
  }, [workspaceDataNeeded, row.league?.id, row.league_id, row.season_id, strengthRanking]);

  useEffect(() => {
    if (!workspaceDataNeeded) return undefined;
    let cancelled = false;
    setPreviousSeasonData({ loading: true, season: null, actualRows: [] });
    loadPreviousLeagueSeason(row.season_id)
      .then(async ({ currentSeason, previousSeason }) => {
        if (!previousSeason) return { season: null, actualRows: [] };
        const response = await fetchViaPost("get-season-base-stats", JSON.stringify({ league_id: Number(currentSeason.league_id), season_id: Number(previousSeason.id), fast_read: true }));
        const result = await response.json();
        return { season: previousSeason, actualRows: result?.record?.actual_table || [] };
      })
      .then((data) => { if (!cancelled) setPreviousSeasonData({ loading: false, ...data }); })
      .catch(() => { if (!cancelled) setPreviousSeasonData({ loading: false, season: null, actualRows: [] }); });
    return () => { cancelled = true; };
  }, [workspaceDataNeeded, row.season_id]);

  useEffect(() => {
    if (!workspaceDataNeeded || !["workspace-full", "experiment"].includes(adminFixtureView)) return undefined;
    if (row.market_derived) {
      setMarketDerivedPreview(null);
      return undefined;
    }
    let cancelled = false;
    setMarketDerivedPreview(null);
    setMarketPreviewReason(null);
    fetchFixturePrematchContext(row.id).then((result) => {
      if (!cancelled) {
        setMarketDerivedPreview(result?.market_derived || null);
        setMarketPreviewReason(result?.reason || (result?.market_derived ? null : "market_preview_unavailable"));
      }
    }).catch(() => { if (!cancelled) {setMarketDerivedPreview(null);setMarketPreviewReason("market_preview_read_failed");} });
    return () => { cancelled = true; };
  }, [workspaceDataNeeded, adminFixtureView, row.id, row.season_id, row.localTeam?.id, row.visitorTeam?.id, row.market_derived]);

  useEffect(() => {
    if (!workspaceDataNeeded) return undefined;
    if ((Array.isArray(actualRows) || Array.isArray(expectedRows) || Array.isArray(shouldRows)) && Array.isArray(marketRows)) {
      setPerformance({
        actualRows: actualRows || [],
        expectedRows: expectedRows || [],
        shouldRows: shouldRows || [],
        marketRows,
        expectedLedger: expectedLedger || [],
        shouldLedger: shouldLedger || [],
      });
      return;
    }
    let cancelled = false;
    const leagueId = Number(row.league_id ?? row.league?.id);
    if (!Number.isFinite(leagueId) || !Number.isFinite(Number(row.season_id))) return undefined;
    Promise.all([
      fetchViaPost("get-season-base-stats", JSON.stringify({ league_id: leagueId, season_id: Number(row.season_id), fast_read: true })).then((response) => response.json()),
      fetchViaPost("build-user-season-stats", JSON.stringify({ mode: "load", league_id: leagueId, season_id: Number(row.season_id) })).then((response) => response.json()),
    ]).then(([base, personal]) => {
      if (!cancelled) setPerformance({
        actualRows: base?.record?.actual_table || [],
        expectedRows: personal?.record?.expected_table || [],
        shouldRows: personal?.record?.should_table || [],
        marketRows: personal?.record?.market_table || [],
        expectedLedger: personal?.record?.expected?.ledger || [],
        shouldLedger: personal?.record?.should?.ledger || [],
      });
    }).catch(() => {
      if (!cancelled) setPerformance({ actualRows: [], expectedRows: [], shouldRows: [], marketRows: [] });
    });
    return () => { cancelled = true; };
  }, [row.id, row.season_id, row.league_id, row.league?.id, actualRows, expectedRows, shouldRows, marketRows, expectedLedger, shouldLedger, workspaceDataNeeded]);

  useEffect(() => {
    if (loadedFixtureId !== row.id || !localTeamStrength || !visitorTeamStrength) return;
    const basis = resolveFixtureModelBasis({modelBasis:row.model_basis, homeTeam:localTeamStrength, awayTeam:visitorTeamStrength, seasonParameters});
    if (workspaceBaseline) {
      if (workspaceBaseline.fixtureDraft.drawFactor === "" && basis.drawFactor != null) {
        setWorkspaceBaseline(previous => ({...previous, fixtureDraft:{...previous.fixtureDraft, drawFactor:basis.drawFactor, goalAverage:basis.goalAverage ?? ""}}));
      }
      return;
    }
    setWorkspaceBaseline({
      homeDraft:{...localTeamStrength}, awayDraft:{...visitorTeamStrength},
      fixtureDraft:{homeGoalExp:row.goalexp?.home ?? "", awayGoalExp:row.goalexp?.away ?? "", drawFactor:basis.drawFactor ?? "", goalAverage:basis.goalAverage ?? ""},
      calculation:{probs:{homewin:row.homewin,draw:row.draw,awaywin:row.awaywin},model_basis:row.model_basis || null},
      row:{...row},
    });
  }, [loadedFixtureId, row, localTeamStrength, visitorTeamStrength, seasonParameters, workspaceBaseline]);

  const [fixtureDetailsView] = useState("classic");
  const [cockpitSection, setCockpitSection] = useState("edit");

  const cockpitAccess = getFeatureAccess(
    FEATURES.MATCH_ANALYSIS_COCKPIT,
    user,
  );
  if (!localTeamStrength) {
    return (
      <Box sx={{ background: "#192633" }}>
        {actionError && <Alert severity="error">{actionError}</Alert>}
        <Box
          sx={{
            p: "5em",
            display: "flex",
            justifyContent: "center",
            background: "#182531",
          }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  const handleLocalGoalSumExp = ({
    target: { value: goalSumExpNew },
  }) => {
    const goalSum = Number(goalSumExpNew);
    const currentStrength = Number(localTeamStrength?.total_strength);

    if (
      !Number.isFinite(goalSum) ||
      !Number.isFinite(currentStrength)
    ) {
      return;
    }

    const againstTotalNew =
      goalSum / Math.abs(currentStrength + 1);
    const forTotalNew =
      currentStrength * againstTotalNew;

    setLocalGoalSumExp({
      goal_sum_exp: goalSum,
    });

    setLocalTeamStrength((current) => ({
      ...current,
      against_away:
        againstTotalNew *
        (2 - Number(current.against_homefactor)),
      against_home:
        againstTotalNew *
        Number(current.against_homefactor),
      against_total: againstTotalNew,
      for_away:
        forTotalNew *
        (2 - Number(current.for_homefactor)),
      for_home:
        forTotalNew *
        Number(current.for_homefactor),
      for_total: forTotalNew,
    }));

    setLocalGoalSumExpChanged(true);
    setLocalTeamStrengthChanged(true);
  };

  const handleVisitorGoalSumExp = ({
    target: { value: goalSumExpNew },
  }) => {
    const goalSum = Number(goalSumExpNew);
    const currentStrength = Number(visitorTeamStrength?.total_strength);

    if (
      !Number.isFinite(goalSum) ||
      !Number.isFinite(currentStrength)
    ) {
      return;
    }

    const againstTotalNew =
      goalSum / Math.abs(currentStrength + 1);
    const forTotalNew =
      currentStrength * againstTotalNew;

    setVisitorGoalSumExp({
      goal_sum_exp: goalSum,
    });

    setVisitorTeamStrength((current) => ({
      ...current,
      against_away:
        againstTotalNew *
        (2 - Number(current.against_homefactor)),
      against_home:
        againstTotalNew *
        Number(current.against_homefactor),
      against_total: againstTotalNew,
      for_away:
        forTotalNew *
        (2 - Number(current.for_homefactor)),
      for_home:
        forTotalNew *
        Number(current.for_homefactor),
      for_total: forTotalNew,
    }));

    setVisitorGoalSumExpChanged(true);
    setVisitorTeamStrengthChanged(true);
  };

  const handleLocalTeamStrengthChange = ({
    target: { value: totalStrengthNew },
  }) => {
    const strength = Number(totalStrengthNew);

    if (!Number.isFinite(strength)) {
      return;
    }

    setLocalTeamStrength((current) =>
      updateFixtureTeamStrength(current, strength),
    );

    setLocalTeamStrengthChanged(true);
    setLocalGoalSumExpChanged(true);
  };

  const handleVisitorTeamStrengthChange = ({
    target: { value: totalStrengthNew },
  }) => {
    const strength = Number(totalStrengthNew);

    if (!Number.isFinite(strength)) {
      return;
    }

    setVisitorTeamStrength((current) =>
      updateFixtureTeamStrength(current, strength),
    );

    setVisitorTeamStrengthChanged(true);
    setVisitorGoalSumExpChanged(true);
  };

  const formatValue = (value, digits = 3) => {
    const number = Number(value);

    if (!Number.isFinite(number)) {
      return "–";
    }

    return number.toFixed(digits);
  };

  const settingSignal = (
    currentValue,
    observedValue,
    tolerance = 0.025,
  ) => {
    const current = Number(currentValue);
    const observed = Number(observedValue);

    if (
      !Number.isFinite(current) ||
      !Number.isFinite(observed)
    ) {
      return {
        symbol: "–",
        text: "noch keine Vergleichsbasis",
        detail: "Für diesen Vergleich liegen noch keine ausreichenden Werte vor.",
      };
    }

    const difference = observed - current;

    if (Math.abs(difference) <= tolerance) {
      return {
        symbol: "≈",
        text: "nahe an der Einstellung",
        detail:
          `Erspielt ${formatValue(observed)} · ` +
          `Einstellung ${formatValue(current)}`,
      };
    }

    if (difference > 0) {
      return {
        symbol: "↑",
        text: "eher höher prüfen",
        detail:
          `Erspielt ${formatValue(observed)} liegt über ` +
          `Einstellung ${formatValue(current)}.`,
      };
    }

    return {
      symbol: "↓",
      text: "eher niedriger prüfen",
      detail:
        `Erspielt ${formatValue(observed)} liegt unter ` +
        `Einstellung ${formatValue(current)}.`,
    };
  };

  const localStrengthSignal = settingSignal(
    localTeamStrength?.total_strength,
    earnedStrengthHome,
  );

  const visitorStrengthSignal = settingSignal(
    visitorTeamStrength?.total_strength,
    earnedStrengthAway,
  );

  const localGoalSignal = settingSignal(
    localGoalSumExp?.goal_sum_exp,
    earnedGoalSumHome,
    0.075,
  );

  const visitorGoalSignal = settingSignal(
    visitorGoalSumExp?.goal_sum_exp,
    earnedGoalSumAway,
    0.075,
  );

  const hasUnsavedSettings =
    localTeamStrengthChanged ||
    localGoalSumExpChanged ||
    visitorTeamStrengthChanged ||
    visitorGoalSumExpChanged;

  const handleTeamDraftChange = (side, field, value) => {
    const setter = side === "home" ? setLocalTeamStrength : setVisitorTeamStrength;
    setter((current) => computeInterdependentValues(
      { ...current, [field]: Number(value) },
      current,
    ));
    if (side === "home") {
      setLocalTeamStrengthChanged(true);
      setLocalGoalSumExpChanged(true);
    } else {
      setVisitorTeamStrengthChanged(true);
      setVisitorGoalSumExpChanged(true);
    }
    setDraftCalculation(null);
    setDraftReady(false);
    setFixtureEstimateSaved(false);
  };

  const openWorkspace = () => setAdminFixtureView("workspace");

  const resetWorkspace = () => {
    if (!workspaceBaseline) return;
    setLocalTeamStrength({ ...workspaceBaseline.homeDraft });
    setVisitorTeamStrength({ ...workspaceBaseline.awayDraft });
    setLocalGoalSumExp({goal_sum_exp:Number((Number(workspaceBaseline.homeDraft.for_total)+Number(workspaceBaseline.homeDraft.against_total)).toFixed(3))});
    setVisitorGoalSumExp({goal_sum_exp:Number((Number(workspaceBaseline.awayDraft.for_total)+Number(workspaceBaseline.awayDraft.against_total)).toFixed(3))});
    setFixtureDraft({ ...workspaceBaseline.fixtureDraft });
    setLocalTeamStrengthChanged(false);
    setLocalGoalSumExpChanged(false);
    setVisitorTeamStrengthChanged(false);
    setVisitorGoalSumExpChanged(false);
    setFixtureOverrideChanged({ homeGoalExp: false, awayGoalExp: false, drawFactor: false });
    setDraftCalculation(workspaceBaseline.calculation);
    setDraftReady(false);
    setFixtureEstimateSaved(false);
    onDraftReset?.(row.id, workspaceBaseline.row);
  };

  const adminViewSwitch = (
    <>
    {actionError && <Alert severity="error">{actionError}</Alert>}
    <AdminFixtureViewToggle
      user={user}
      value={adminFixtureView}
      onChange={(value) => {
        if (value === "experiment") {
          setPreviousView(adminFixtureView);
          previousScroll.current = [[window, window.scrollX, window.scrollY]];
          for (let element = document.activeElement; element && element !== document.body; element = element.parentElement) {
            if (element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth) previousScroll.current.push([element, element.scrollLeft, element.scrollTop]);
          }
        }
        if (value !== "legacy") openWorkspace();
        if (value === "legacy") setAdminFixtureView("legacy");
        else setAdminFixtureView(value);
      }}
    />
    </>
  );

  const Workspace = adminFixtureView === "experiment" ? ExperimentalFixtureWorkspace : FixtureWorkspacePanel;
  if (expandedWorkspaceAllowed && (adminFixtureView === "workspace" || (adminFixtureView === "experiment" && canUseFixtureWorkspaceView("experiment", user)))) {
    return (
      <Box sx={{ bgcolor: "#142633" }}>
        {adminViewSwitch}
        <Workspace
      actionError={actionError}
      user={user}
      onClose={closeExperiment}
      row={{...row, market_derived_preview: marketDerivedPreview, market_preview_reason:marketPreviewReason}}
      homeDraft={localTeamStrength}
      awayDraft={visitorTeamStrength}
      fixtureDraft={fixtureDraft}
      onTeamChange={handleTeamDraftChange}
      onFixtureChange={(field, value) => {
        setFixtureDraft((current) => ({ ...current, [field]: value }));
        setFixtureOverrideChanged((current) => ({ ...current, [field]: true }));
        setDraftCalculation(null);
        setDraftReady(false);
        setFixtureEstimateSaved(false);
      }}
      onRecompile={() => runAction(async () => {
        const compiled = await handleRecompile(
          row.id,
          row.season_id,
          localTeamStrength,
          visitorTeamStrength,
          {
            home_goal_expectancy: fixtureOverrideChanged.homeGoalExp ? fixtureDraft.homeGoalExp : null,
            away_goal_expectancy: fixtureOverrideChanged.awayGoalExp ? fixtureDraft.awayGoalExp : null,
            draw_factor: fixtureOverrideChanged.drawFactor ? fixtureDraft.drawFactor : null,
          },
          false,
        );
        if (!compiled?.model_basis) throw new Error(compiled?.error || "PREVIEW_RESULT_MISSING");
        if (compiled?.model_basis) {
          setFixtureDraft({
            homeGoalExp: compiled.model_basis.home_goal_expectancy,
            awayGoalExp: compiled.model_basis.away_goal_expectancy,
            drawFactor: compiled.model_basis.draw_factor,
            goalAverage: compiled.model_basis.goal_average,
          });
          setDraftCalculation(compiled);
          setDraftReady(true);
          setFixtureEstimateSaved(false);
        }
      })}
      onSaveFixtureEstimate={() => runAction(async () => {
        if (!draftCalculation?.model_basis) return;
        await handleRecompile(
          row.id,
          row.season_id,
          localTeamStrength,
          visitorTeamStrength,
          {
            home_goal_expectancy: draftCalculation.model_basis.home_goal_expectancy,
            away_goal_expectancy: draftCalculation.model_basis.away_goal_expectancy,
            draw_factor: draftCalculation.model_basis.draw_factor,
          },
          true,
        );
        setFixtureEstimateSaved(true);
        setDraftReady(false);
      })}
      onSaveTeams={handleSave}
      onReset={resetWorkspace}
      hasUnsavedTeams={hasUnsavedSettings}
      hasCalculatedDraft={draftReady}
      hasFixtureDraftChanges={Object.values(fixtureOverrideChanged).some(Boolean)}
      fixtureEstimateSaved={fixtureEstimateSaved}
      calculation={draftCalculation}
      performance={performance}
      marketOdds={marketOdds}
      indicationThreshold={indicationThreshold}
      seasonParameters={seasonParameters}
      standingStats={stats}
      strengthRanking={workspaceStrengthRanking}
      previousSeasonData={previousSeasonData}
      formContent={Object.keys(stats || {}).length ? <LeagueTable stats={stats} localteamId={row.localTeam.id} localTeam={row.localTeam} visitorteamId={row.visitorTeam.id} visitorTeam={row.visitorTeam} seasonId={row.season_id} beforeStartingAt={row.starting_at} /> : null}
        />
      </Box>
    );
  }

  if (canUseFixtureWorkspaceView("workspace-full", user) && adminFixtureView === "workspace-full") {
    return <Box sx={{ bgcolor: "#142633" }}>
      {adminViewSwitch}
      <FixtureFullView
        row={{ ...row, market_derived_preview: marketDerivedPreview }}
        homeDraft={localTeamStrength}
        awayDraft={visitorTeamStrength}
        fixtureDraft={fixtureDraft}
        calculation={draftCalculation}
        performance={performance}
        marketOdds={marketOdds}
        indicationThreshold={indicationThreshold}
        seasonParameters={seasonParameters}
        strengthRanking={workspaceStrengthRanking}
        previousSeasonData={previousSeasonData}
        formContent={Object.keys(stats || {}).length ? <LeagueTable stats={stats} localteamId={row.localTeam.id} localTeam={row.localTeam} visitorteamId={row.visitorTeam.id} visitorTeam={row.visitorTeam} seasonId={row.season_id} beforeStartingAt={row.starting_at} /> : null}
      />
    </Box>;
  }

  const signalCell = (signal) => (
    <Box
      title={signal.detail}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 0.75,
        minHeight: 36,
        fontWeight: 700,
      }}
    >
      <Box
        component="span"
        sx={{
          fontSize: "1.25rem",
          lineHeight: 1,
        }}
      >
        {signal.symbol}
      </Box>
      <Box component="span">
        {signal.text}
      </Box>
    </Box>
  );

  const viewSwitch = adminViewSwitch;

  const comparisonRow = (
    label,
    homeValue,
    awayValue,
    options = {},
  ) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(150px, 0.8fr) 1fr 1fr",
        alignItems: "center",
        minHeight: 42,
        borderTop: "1px solid rgba(0,0,0,0.08)",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          fontWeight: 600,
          color: "rgba(0,0,0,0.72)",
        }}
      >
        {label}
      </Box>

      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          textAlign: "center",
          background:
            options.highlight
              ? "rgba(255,181,99,0.12)"
              : "transparent",
        }}
      >
        {homeValue}
      </Box>

      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          textAlign: "center",
          background:
            options.highlight
              ? "rgba(255,181,99,0.12)"
              : "transparent",
        }}
      >
        {awayValue}
      </Box>
    </Box>
  );

  if (
    (compactWorkspace || fixtureDetailsView === "cockpit") &&
    (compactWorkspace || cockpitAccess.allowed)
  ) {
    return (
      <Box sx={{ bgcolor:"background.paper", color:"text.primary" }}>
        {viewSwitch}

        <Box
          sx={{
            px: compactWorkspace ? 1.5 : 2,
            pt: compactWorkspace ? 0.5 : 1,
            background: "#182531",
          }}
        >
          {compactWorkspace && (
            <Typography variant="caption" sx={{ fontWeight: 900 }}>
              Bearbeiten
            </Typography>
          )}
          {!compactWorkspace && <ToggleButtonGroup
            size="small"
            exclusive
            value={cockpitSection}
            onChange={(_, value) => {
              if (value) {
                setCockpitSection(value);
              }
            }}
            sx={{ mb: 1.5 }}
          >
            <ToggleButton value="edit">
              Bearbeiten
            </ToggleButton>

            <ToggleButton value="statistics">
              Analyse & Tabellen
            </ToggleButton>
          </ToggleButtonGroup>}
        </Box>

        {compactWorkspace || cockpitSection === "edit" ? (
          <Box
            sx={{
              p: compactWorkspace ? 0.75 : 1.5,
              pt: 0.5,
              background:
                "linear-gradient(180deg, rgba(19,27,38,0.97) 0%, rgba(31,42,56,0.96) 100%)",
              color: "white",
            }}
          >
            <Box
              sx={{
                mb: compactWorkspace ? 0.65 : 1.25,
                px: compactWorkspace ? 1 : 1.5,
                py: compactWorkspace ? 0.6 : 1.1,
                display: "grid",
                gridTemplateColumns: "1fr auto 1fr",
                gap: compactWorkspace ? 0.5 : 1,
                alignItems: "center",
                borderRadius: 1.5,
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.16)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minWidth: 0,
                }}
              >
                <img
                  src={row.localTeam.image_path}
                  style={{
                    width: 34,
                    height: 34,
                    objectFit: "contain",
                  }}
                  alt=""
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.localTeam.name}
                </Typography>
              </Box>

              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 5,
                  fontWeight: 900,
                  letterSpacing: 1,
                  background: "rgba(255,181,99,0.95)",
                  color: "#18202b",
                }}
              >
                VS
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 1,
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    textAlign: "right",
                  }}
                >
                  {row.visitorTeam.name}
                </Typography>

                <img
                  src={row.visitorTeam.image_path}
                  style={{
                    width: 34,
                    height: 34,
                    objectFit: "contain",
                  }}
                  alt=""
                />
              </Box>
            </Box>

            <Box
              sx={{
                mb: compactWorkspace ? 0.65 : 1.25,
                px: 1.5,
                py: 0.8,
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                alignItems: "center",
                borderRadius: 1,
                background: "rgba(255,181,99,0.13)",
                borderLeft: "4px solid rgb(255,181,99)",
              }}
            >
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 900 }}
                >
                  Team Settings bearbeiten
                </Typography>

                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.72)" }}
                >
                  Einstellung prüfen → bei Bedarf ändern →
                  neu berechnen → Wirkung auf die Quote kontrollieren.
                </Typography>
              </Box>

              <Box
                sx={{
                  px: 1.1,
                  py: 0.5,
                  borderRadius: 1,
                  whiteSpace: "nowrap",
                  fontSize: "0.78rem",
                  fontWeight: 800,
                  background: hasUnsavedSettings
                    ? "rgba(255,181,99,0.95)"
                    : "rgba(255,255,255,0.12)",
                  color: hasUnsavedSettings
                    ? "#18202b"
                    : "rgba(255,255,255,0.78)",
                }}
              >
                {hasUnsavedSettings ? "ÄNDERUNGEN OFFEN" : ""}
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: compactWorkspace
                    ? "minmax(0, 1fr) minmax(0, 1fr) 280px"
                    : "1fr 1fr",
                },
                gap: compactWorkspace ? 0.75 : 1.25,
              }}
            >
              <Box
                sx={{
                  borderRadius: 1.5,
                  overflow: "hidden",
                  background: "#223140",
                  color: "#edf2f7",
                  border: "2px solid rgba(66,165,245,0.75)",
                  boxShadow: "0 5px 18px rgba(0,0,0,0.22)",
                }}
              >
                <Box
                  sx={{
                    px: 1.4,
                    py: 0.75,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: "rgba(66,165,245,0.15)",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: 900,
                      letterSpacing: 0.9,
                    }}
                  >
                    HEIMTEAM
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 800 }}
                  >
                    {row.localTeam.name}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 1.25,
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: 1.25,
                    alignItems: "center",
                    borderBottom: "1px solid rgba(0,0,0,0.09)",
                    background: "#192633",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 900,
                        letterSpacing: 0.8,
                        color: "text.secondary",
                      }}
                    >
                      SPIELSTÄRKE / QUOTIENT
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      gespeicherte Berechnungsbasis
                    </Typography>
                  </Box>

                  <TextField
                    onChange={handleLocalTeamStrengthChange}
                    onFocus={(event) => event.target.select()}
                    type="number"
                    size="small"
                    value={formatValue(
                      localTeamStrength.total_strength,
                    )}
                    inputProps={{
                      step: "0.005",
                      style: {
                        textAlign: "center",
                        fontSize: "1.3rem",
                        fontWeight: 900,
                      },
                    }}
                    sx={{
                      width: 126,
                      "& .MuiOutlinedInput-root": {
                        background: "#293746",
                      },
                    }}
                    title={i18next.t(
                      "help.tooltips.fixtureStoredStrength",
                    )}
                  />
                </Box>

                <Box
                  sx={{
                    px: 1.25,
                    py: 0.9,
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    alignItems: "center",
                    gap: 1,
                    background: "rgba(66,165,245,0.06)",
                  }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        fontWeight: 800,
                      }}
                    >
                      Erspielt
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "1.05rem",
                        fontWeight: 900,
                      }}
                    >
                      {formatValue(earnedStrengthHome)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontWeight: 900,
                      background: "rgba(0,0,0,0.055)",
                    }}
                    title={localStrengthSignal.detail}
                  >
                    {localStrengthSignal.symbol}{" "}
                    {localStrengthSignal.text}
                  </Box>
                </Box>

                <Box
                  sx={{
                    p: 1.25,
                    mt: 0.4,
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: 1.25,
                    alignItems: "center",
                    borderTop: "1px solid rgba(0,0,0,0.09)",
                    borderBottom: "1px solid rgba(0,0,0,0.09)",
                    background: "#192633",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 900,
                        letterSpacing: 0.8,
                        color: "text.secondary",
                      }}
                    >
                      TORSUMME / TORNIVEAU
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      gespeicherte Berechnungsbasis
                    </Typography>
                  </Box>

                  {row.goalexp?.home?.toFixed(3) ? (
                    <TextField
                      onChange={handleLocalGoalSumExp}
                      onFocus={(event) => event.target.select()}
                      type="number"
                      size="small"
                      value={formatValue(
                        localGoalSumExp?.goal_sum_exp,
                      )}
                      inputProps={{
                        step: "0.005",
                        style: {
                          textAlign: "center",
                          fontSize: "1.3rem",
                          fontWeight: 900,
                        },
                      }}
                      sx={{
                        width: 126,
                        "& .MuiOutlinedInput-root": {
                          background: "#293746",
                        },
                      }}
                      title={i18next.t(
                        "help.tooltips.fixtureStoredGoalSum",
                      )}
                    />
                  ) : (
                    <Typography variant="caption">
                      noch nicht kompiliert
                    </Typography>
                  )}
                </Box>

                <Box
                  sx={{
                    px: 1.25,
                    py: 0.9,
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: 1,
                    alignItems: "center",
                    background: "rgba(255,181,99,0.10)",
                  }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        fontWeight: 800,
                      }}
                    >
                      Erspielte Torsumme
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "1.05rem",
                        fontWeight: 900,
                      }}
                    >
                      {formatValue(earnedGoalSumHome)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontWeight: 900,
                      background: "rgba(0,0,0,0.055)",
                    }}
                    title={localGoalSignal.detail}
                  >
                    {localGoalSignal.symbol}{" "}
                    {localGoalSignal.text}
                  </Box>
                </Box>

                <Box
                  sx={{
                    px: 1.25,
                    py: 0.85,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: "#223140",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 800 }}
                  >
                    Erwartete Tore in diesem Spiel
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "1.05rem",
                      fontWeight: 900,
                    }}
                  >
                    {formatValue(row.goalexp?.home)}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  borderRadius: 1.5,
                  overflow: "hidden",
                  background: "#223140",
                  color: "#edf2f7",
                  border: "2px solid rgba(239,83,80,0.72)",
                  boxShadow: "0 5px 18px rgba(0,0,0,0.22)",
                }}
              >
                <Box
                  sx={{
                    px: 1.4,
                    py: 0.75,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: "rgba(239,83,80,0.13)",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: 900,
                      letterSpacing: 0.9,
                    }}
                  >
                    AUSWÄRTSTEAM
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 800 }}
                  >
                    {row.visitorTeam.name}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 1.25,
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: 1.25,
                    alignItems: "center",
                    borderBottom: "1px solid rgba(0,0,0,0.09)",
                    background: "#192633",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 900,
                        letterSpacing: 0.8,
                        color: "text.secondary",
                      }}
                    >
                      SPIELSTÄRKE / QUOTIENT
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      gespeicherte Berechnungsbasis
                    </Typography>
                  </Box>

                  <TextField
                    onChange={handleVisitorTeamStrengthChange}
                    onFocus={(event) => event.target.select()}
                    type="number"
                    size="small"
                    value={formatValue(
                      visitorTeamStrength.total_strength,
                    )}
                    inputProps={{
                      step: "0.005",
                      style: {
                        textAlign: "center",
                        fontSize: "1.3rem",
                        fontWeight: 900,
                      },
                    }}
                    sx={{
                      width: 126,
                      "& .MuiOutlinedInput-root": {
                        background: "#293746",
                      },
                    }}
                    title={i18next.t(
                      "help.tooltips.fixtureStoredStrength",
                    )}
                  />
                </Box>

                <Box
                  sx={{
                    px: 1.25,
                    py: 0.9,
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    alignItems: "center",
                    gap: 1,
                    background: "rgba(239,83,80,0.05)",
                  }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        fontWeight: 800,
                      }}
                    >
                      Erspielt
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "1.05rem",
                        fontWeight: 900,
                      }}
                    >
                      {formatValue(earnedStrengthAway)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontWeight: 900,
                      background: "rgba(0,0,0,0.055)",
                    }}
                    title={visitorStrengthSignal.detail}
                  >
                    {visitorStrengthSignal.symbol}{" "}
                    {visitorStrengthSignal.text}
                  </Box>
                </Box>

                <Box
                  sx={{
                    p: 1.25,
                    mt: 0.4,
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: 1.25,
                    alignItems: "center",
                    borderTop: "1px solid rgba(0,0,0,0.09)",
                    borderBottom: "1px solid rgba(0,0,0,0.09)",
                    background: "#192633",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 900,
                        letterSpacing: 0.8,
                        color: "text.secondary",
                      }}
                    >
                      TORSUMME / TORNIVEAU
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      gespeicherte Berechnungsbasis
                    </Typography>
                  </Box>

                  {row.goalexp?.away?.toFixed(3) ? (
                    <TextField
                      onChange={handleVisitorGoalSumExp}
                      onFocus={(event) => event.target.select()}
                      type="number"
                      size="small"
                      value={formatValue(
                        visitorGoalSumExp?.goal_sum_exp,
                      )}
                      inputProps={{
                        step: "0.005",
                        style: {
                          textAlign: "center",
                          fontSize: "1.3rem",
                          fontWeight: 900,
                        },
                      }}
                      sx={{
                        width: 126,
                        "& .MuiOutlinedInput-root": {
                          background: "#293746",
                        },
                      }}
                      title={i18next.t(
                        "help.tooltips.fixtureStoredGoalSum",
                      )}
                    />
                  ) : (
                    <Typography variant="caption">
                      noch nicht kompiliert
                    </Typography>
                  )}
                </Box>

                <Box
                  sx={{
                    px: 1.25,
                    py: 0.9,
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: 1,
                    alignItems: "center",
                    background: "rgba(255,181,99,0.10)",
                  }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        fontWeight: 800,
                      }}
                    >
                      Erspielte Torsumme
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "1.05rem",
                        fontWeight: 900,
                      }}
                    >
                      {formatValue(earnedGoalSumAway)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontWeight: 900,
                      background: "rgba(0,0,0,0.055)",
                    }}
                    title={visitorGoalSignal.detail}
                  >
                    {visitorGoalSignal.symbol}{" "}
                    {visitorGoalSignal.text}
                  </Box>
                </Box>

                <Box
                  sx={{
                    px: 1.25,
                    py: 0.85,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: "#223140",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 800 }}
                  >
                    Erwartete Tore in diesem Spiel
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "1.05rem",
                      fontWeight: 900,
                    }}
                  >
                    {formatValue(row.goalexp?.away)}
                  </Typography>
                </Box>
              </Box>

              {compactWorkspace && (
                <StrengthRanking
                  rows={strengthRanking}
                  selectedTeamIds={[row.localTeam.id, row.visitorTeam.id]}
                />
              )}
            </Box>

            {!compactWorkspace && <Box
              sx={{
                mt: 1.25,
                p: 1,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                flexWrap: "wrap",
                borderRadius: 1,
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.12)",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  mr: 0.5,
                  fontWeight: 900,
                  color: "rgba(255,255,255,0.8)",
                }}
              >
                WEITER PRÜFEN:
              </Typography>

              {[
                "Actual / Expected / Should",
                "Letzte Spiele",
                "Heim / Auswärts",
                "Tabelle",
                "Stärke-Ranking",
              ].map((label) => (
                <Box
                  key={label}
                  sx={{
                    px: 1,
                    py: 0.45,
                    borderRadius: 4,
                    fontSize: "0.75rem",
                    fontWeight: 800,
                    background: "rgba(255,255,255,0.10)",
                    border: "1px solid rgba(255,255,255,0.16)",
                  }}
                >
                  {label}
                </Box>
              ))}
            </Box>}

            <Box
              sx={{
                mt: 1.25,
                p: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                borderRadius: 1.5,
                background: "rgba(0,0,0,0.24)",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.72)" }}
              >
                Nach einer Änderung zuerst neu berechnen.
                Danach sehen wir, ob die Marktindikation
                bestehen bleibt.
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  title={i18next.t(
                    "help.tooltips.fixtureRecompile",
                  )}
                  variant="contained"
                  color="warning"
                  onClick={() => runAction(() =>
                    handleRecompile(
                      row.id,
                      row.season_id,
                      localTeamStrength,
                      visitorTeamStrength,
                    ))
                  }
                  sx={{ fontWeight: 900 }}
                >
                  <Calculate />
                  <Box sx={{ ml: 0.5 }}>
                    Neu berechnen
                  </Box>
                </Button>

                <Button
                  title={i18next.t(
                    "help.tooltips.fixtureSaveSettings",
                  )}
                  variant="contained"
                  disabled={!hasUnsavedSettings}
                  onClick={() => handleSave()}
                  sx={{ fontWeight: 800 }}
                >
                  <Save />
                  <Box sx={{ ml: 0.5 }}>
                    Speichern
                  </Box>
                </Button>

                <Button
                  title={i18next.t(
                    "help.tooltips.fixtureResetChanges",
                  )}
                  variant="outlined"
                  disabled={!hasUnsavedSettings}
                  onClick={() => {
                    setLocalTeamStrengthChanged(false);
                    setLocalGoalSumExpChanged(false);
                    setLocalGoalSumExp(
                      originalLocalGoalSumExp,
                    );
                    setLocalTeamStrength(
                      originalLocalTeamStrength,
                    );

                    setVisitorTeamStrengthChanged(false);
                    setVisitorGoalSumExpChanged(false);
                    setVisitorGoalSumExp(
                      originalVisitorGoalSumExp,
                    );
                    setVisitorTeamStrength(
                      originalVisitorTeamStrength,
                    );
                  }}
                  sx={{
                    fontWeight: 800,
                    color: "white",
                    borderColor: "rgba(255,255,255,0.45)",
                  }}
                >
                  <RestartAlt />
                  <Box sx={{ ml: 0.5 }}>
                    Verwerfen
                  </Box>
                </Button>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              p: 2,
              pt: 0,
              background: "#182531",
            }}
          >
            <Box
              sx={{
                mb: 1.25,
                px: 1.5,
                py: 1,
                background: "#192633",
                border: "1px solid rgba(0,0,0,0.12)",
                borderRadius: 1,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700 }}
              >
                Analyse & Tabellen
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Hier liegen die tieferen Vergleichsebenen:
                Actual, Expected und Should, Tabellen,
                letzte Spiele sowie weitere Statistik.
                Diese Ebene dient der Prüfung und verändert
                die Team-Settings nicht automatisch.
              </Typography>
            </Box>

            <Box
              sx={{
                background: "#192633",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <LeagueTable
                stats={stats}
                localteamId={row.localTeam.id}
                localTeam={row.localTeam}
                visitorteamId={row.visitorTeam.id}
                visitorTeam={row.visitorTeam}
                seasonId={row.season_id}
              />
            </Box>
          </Box>
        )}

        <Divider style={{ height: "2em" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor:"background.paper", color:"text.primary" }}>
      {viewSwitch}
      <Box
        sx={{ p: 2 }}
        style={{
          background: "#182531",
          flexDirection: "column",
          display: "flex",
          gap: 5,
        }}
      >
        <Box style={{ display: "flex" }}>
          <Box style={{ flex: 1 }}>
            <Typography
              component="h1"
              variant="h5"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5em",
                justifyContent: "center",
              }}
            >
              <img
                src={row.localTeam.image_path}
                style={{ height: "1.5em" }}
                alt=""
              />
              {row.localTeam.name}
            </Typography>
            <Box style={{ display: "block", marginTop: "0.5em" }}>
              <Box
                style={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "space-around",
                }}
              >
                <TextField
                  onChange={handleLocalTeamStrengthChange}
                  style={{ margin: "5px", marginTop: "9px" }}
                  id="outlined-number"
                  label="Stored strength"
                  title={i18next.t("help.tooltips.fixtureStoredStrength")}
                  type="number"
                  value={(+localTeamStrength.total_strength).toFixed(3)}
                  min="0"
                  inputProps={{ step: "0.005" }}
                />
                {row.goalexp?.home?.toFixed(3) ? (
                  <TextField
                    onChange={handleLocalGoalSumExp}
                    style={{ margin: "5px", marginTop: "9px" }}
                    id="outlined-number"
                    label="Stored goal sum (exp)"
                    title={i18next.t("help.tooltips.fixtureStoredGoalSum")}
                    type="number"
                    value={+localGoalSumExp.goal_sum_exp} //{row.goalexp?.home?.toFixed(3) ?? "-"}
                    min="0"
                    inputProps={{ step: "0.005" }}
                  />
                ) : (
                  <TextField
                    onChange={() => {}}
                    style={{ margin: "5px", marginTop: "9px" }}
                    label="Stored goal sum (exp)"
                    title={i18next.t("help.tooltips.fixtureStoredGoalSum")}
                    disabled
                    value="Compile the match first"
                  />
                )}
              </Box>
            </Box>

            <Box style={{ display: "flex", marginTop: "0.5em" }}>
                <Box
                  style={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "space-around",
                  }}
                >
                  {earnedStrengthHome !== null && !isNaN(earnedStrengthHome) ? (
                    <TextField
                      id="outlined-number"
                      label="Earned strength"
                      style={{ margin: "5px" }}
                      disabled
                      type="number"
                      value={earnedStrengthHome.toFixed(3)}
                      min="0"
                    />
                  ) : (
                    ""
                  )}
                  {row.goalexp?.home?.toFixed(3) ? (
                    earnedGoalSumHome !== null && !isNaN(earnedGoalSumHome) ? (
                      <TextField
                        id="outlined-number"
                        label="Earned goal sum"
                        style={{ margin: "5px" }}
                        type="number"
                        disabled
                        value={earnedGoalSumHome.toFixed(3)} //{row.goalexp?.home?.toFixed(3) ?? "-"}
                        min="0"
                      />
                    ) : (
                      ""
                    )
                  ) : (
                    <TextField
                      onChange={() => {}}
                      label="Goal sum exp home team"
                      style={{ margin: "5px" }}
                      disabled
                      value="Compile the match first"
                    />
                  )}
                </Box>
              </Box>
            <Box
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "space-around",
                marginTop: "0.5em",
              }}
            >
              {row.goalexp?.home?.toFixed(3) ? (
                <TextField
                  onChange={() => {}}
                  id="outlined-number"
                  label="Home goal exp"
                  title={i18next.t("help.tooltips.fixtureHomeGoalExpectancy")}
                  disabled
                  type="number"
                  value={row.goalexp?.home?.toFixed(3) ?? "-"}
                  min="0"
                  inputProps={{ step: "0.005" }}
                />
              ) : (
                <TextField
                  onChange={() => {}}
                  title={i18next.t("help.tooltips.fixtureHomeGoalExpectancy")}
                  label="Home goal exp"
                  disabled
                  value="Compile the match first"
                />
              )}
            </Box>
          </Box>
          <Box style={{ flex: 1 }}>
            <Typography
              component="h1"
              variant="h5"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5em",
                justifyContent: "center",
              }}
            >
              <img
                src={row.visitorTeam.image_path}
                style={{ height: "1.5em" }}
                alt=""
              />
              {row.visitorTeam.name}
            </Typography>
            <Box style={{ display: "flex", marginTop: "0.5em" }}>
              <Box
                style={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "space-around",
                }}
              >
                <TextField
                  onChange={handleVisitorTeamStrengthChange}
                  id="outlined-number"
                  label="Stored strength"
                  style={{ margin: "5px", marginTop: "9px" }}
                  title={i18next.t("help.tooltips.fixtureStoredStrength")}
                  type="number"
                  value={(+visitorTeamStrength.total_strength).toFixed(3)}
                  min="0"
                  inputProps={{ step: "0.005" }}
                />
                {row.goalexp?.away?.toFixed(3) ? (
                  <TextField
                    onChange={handleVisitorGoalSumExp}
                    id="outlined-number"
                    label="Stored goal sum (exp)"
                    style={{ margin: "5px", marginTop: "9px" }}
                    title={i18next.t("help.tooltips.fixtureStoredGoalSum")}
                    type="number"
                    value={+visitorGoalSumExp.goal_sum_exp} //{row.goalexp?.home?.toFixed(3) ?? "-"}
                    min="0"
                    inputProps={{ step: "0.005" }}
                  />
                ) : (
                  <TextField
                    onChange={() => {}}
                    label="Stored goal sum (exp)"
                    style={{ margin: "5px" }}
                    title={i18next.t("help.tooltips.fixtureStoredGoalSum")}
                    disabled
                    value="Compile the match first"
                  />
                )}
              </Box>
            </Box>
            <Box style={{ display: "flex", marginTop: "0.5em" }}>
                <Box
                  style={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "space-around",
                  }}
                >
                  {earnedStrengthAway !== null && !isNaN(earnedStrengthAway) ? (
                    <TextField
                      id="outlined-number"
                      label="Earned strength"
                      style={{ margin: "5px" }}
                      disabled
                      type="number"
                      value={earnedStrengthAway.toFixed(3)}
                    />
                  ) : (
                    ""
                  )}
                  {row.goalexp?.away?.toFixed(3) ? (
                    earnedGoalSumAway !== null ? (
                      <TextField
                        id="outlined-number"
                        style={{ margin: "5px" }}
                        label="Earned goal sum"
                        type="number"
                        value={earnedGoalSumAway.toFixed(3)} //{row.goalexp?.home?.toFixed(3) ?? "-"}
                        disabled
                      />
                    ) : (
                      ""
                    )
                  ) : (
                    <TextField
                      onChange={() => {}}
                      label="Goal sum exp away team"
                      style={{ margin: "5px" }}
                      disabled
                      value="Compile the match first"
                    />
                  )}
                </Box>
              </Box>
            <Box
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "space-around",
                marginTop: "0.5em",
              }}
            >
              {row.goalexp?.home?.toFixed(3) ? (
                <TextField
                  onChange={() => {}}
                  id="outlined-number"
                  label="Away goal exp"
                  title={i18next.t("help.tooltips.fixtureAwayGoalExpectancy")}
                  disabled
                  type="number"
                  value={row.goalexp?.away?.toFixed(3) ?? "-"}
                  min="0"
                  inputProps={{ step: "0.005" }}
                />
              ) : (
                <TextField
                  onChange={() => {}}
                  title={i18next.t("help.tooltips.fixtureAwayGoalExpectancy")}
                  label="Away goal exp"
                  disabled
                  value="Compile the match first"
                />
              )}
            </Box>
          </Box>
        </Box>
        <Box style={{ display: "flex" }}>
          <Box style={{ flex: 1 }}>
            <LeagueTable
              stats={stats}
              localteamId={row.localTeam.id}
              localTeam={row.localTeam}
              visitorteamId={row.visitorTeam.id}
              visitorTeam={row.visitorTeam}
              seasonId={row.season_id}
            />
          </Box>
          {/*<Box style={{flex: 1}}>
            {<LastGames localTeam={row.localTeam} visitorTeam={row.visitorTeam}/>}
          </Box>*/}
        </Box>
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginTop: "0.5em",
          }}
        >
          <Box style={{ display: "flex", gap: 5 }}>
            <Button
              title={i18next.t("help.tooltips.fixtureSaveSettings")}
              variant="contained"
              onClick={() => handleSave()}
            >
              <Save />
              <Box style={{ marginLeft: ".5em" }}>Save</Box>
            </Button>
            <Button
              title={i18next.t("help.tooltips.fixtureResetChanges")}
              variant="contained"
              onClick={() => {
                setLocalTeamStrengthChanged(false);
                setLocalGoalSumExpChanged(false);
                setLocalGoalSumExp(originalLocalGoalSumExp);
                setLocalTeamStrength(originalLocalTeamStrength);
                setVisitorTeamStrengthChanged(false);
                setVisitorGoalSumExpChanged(false);
                setVisitorGoalSumExp(originalVisitorGoalSumExp);
                setVisitorTeamStrength(originalVisitorTeamStrength);
              }}
            >
              <RestartAlt />
              <Box style={{ marginLeft: ".5em" }}>Reset</Box>
            </Button>
            <Button
              title={i18next.t("help.tooltips.fixtureRecompile")}
              variant="contained"
              onClick={() => runAction(() =>
                handleRecompile(
                  row.id,
                  row.season_id,
                  localTeamStrength,
                  visitorTeamStrength,
                ))
              }
            >
              <Calculate />
              <Box style={{ marginLeft: ".5em" }}>
                {typeof row.homewin === "undefined" ? "Recompile" : "Compile"}
              </Box>
            </Button>
          </Box>
        </Box>
      </Box>
      <Divider style={{ height: "2em" }} />
    </Box>
  );
};
